/**
 * VideoCallCamIcon — co-located visual primitive for `VideoCallBubble`.
 *
 * Renders the video-cam icon with an optional diagonal strike-through
 * overlay (shown when `failed === true`). Extracted from `VideoCallBubble`
 * to keep the bubble file under the 200-line component-size threshold.
 *
 * Flutter source of truth:
 *  - Own variant: `chat_screen.dart:1838-1875` (icon + strike Stack).
 *  - Peer variant: `chat_screen.dart:2978-3001`.
 *  - Strike-through geometry: 1.5 px wide, `actionIconSize + 8` tall,
 *    rotated `-pi / 3` (-60°), centered over the icon (`chat_screen.dart:1850-1858`
 *    / `:2990-2998`).
 *
 * Theme tokens:
 *  - Cam icon tint → `theme.whiteLight` (Flutter `AppColors.whiteLight` at
 *    `chat_screen.dart:1848` / `:2988`).
 *  - Strike-through bar color → `theme.whiteLight` (Flutter
 *    `AppColors.whiteLight` at `chat_screen.dart:1856` / `:2996`).
 *
 * Responsive tokens:
 *  - Icon size → `responsive.icon.large` (= 24) — Flutter
 *    `DimensionConstants.actionIconSize`.
 *  - Strike bar width → `responsive.chat.strikeWidth` (= 1.5).
 *  - Strike bar height → `iconSize + responsive.spacing.sm` (= iconSize + 8) —
 *    Flutter `actionIconSize + 8`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';

interface VideoCallCamIconProps {
  /** When true, renders the diagonal strike-through overlay (call failed). */
  failed: boolean;
}

export const VideoCallCamIcon: React.FC<VideoCallCamIconProps> = ({ failed }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const iconSize = responsive.icon.large;
  const strikeHeight = iconSize + responsive.spacing.sm;

  return (
    <div
      style={{
        position: 'relative',
        width: iconSize,
        height: iconSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AssetIcon
        path="/assets/images/video_cam.png"
        width={iconSize}
        height={iconSize}
        color={theme.whiteLight}
      />
      {failed && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: responsive.chat.strikeWidth,
            height: strikeHeight,
            backgroundColor: theme.whiteLight,
            // Center on the icon, then rotate -60° (Flutter `-pi / 3`).
            transform: 'translate(-50%, -50%) rotate(-60deg)',
            transformOrigin: 'center center',
          }}
        />
      )}
    </div>
  );
};
